// /admin/notifications — notification preferences + org Slack + Teams channels (T128 + T194a + T194b).
//
// Three tabs:
//   1. Preferences — per-user email/Slack-webhook/ntfy toggles + event matrix.
//   2. Slack Channels — org-scoped Slack incoming-webhook channels (T194a, Trial/Enterprise).
//   3. Teams Channels — org-scoped Teams Adaptive Card channels (T194b, Trial/Enterprise).

import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  Bell,
  Check,
  Loader2,
  Mail,
  MessageSquare,
  Plus,
  Save,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import {
  ADMIN_NOTIFICATIONS_PREFS_KEY,
  ADMIN_NOTIFICATION_CHANNELS_KEY,
  NotificationPrefs,
  NotificationPrefsPatch,
  NotificationChannel,
  NotificationChannelCreate,
  adminNotificationsApi,
  notificationChannelsApi,
  fetcher,
} from '../lib/api';
import { PageHeader } from '../components/ui';

// ---------------------------------------------------------------------------
// Page shell with tabs
// ---------------------------------------------------------------------------

type Tab = 'preferences' | 'slack-channels' | 'teams-channels';

export function AdminNotifications() {
  const [tab, setTab] = useState<Tab>('preferences');

  return (
    <div className="max-w-2xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<Bell />}
        title="Notifications"
        description="Choose how you receive alerts and configure org-wide Slack and Teams channels."
      />

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 border-b border-border">
        <TabButton id="preferences" active={tab === 'preferences'} onClick={setTab}>
          Preferences
        </TabButton>
        <TabButton id="slack-channels" active={tab === 'slack-channels'} onClick={setTab}>
          Slack Channels
        </TabButton>
        <TabButton id="teams-channels" active={tab === 'teams-channels'} onClick={setTab}>
          Teams Channels
        </TabButton>
      </div>

      {tab === 'preferences' && <PreferencesTab />}
      {tab === 'slack-channels' && <SlackChannelsTab />}
      {tab === 'teams-channels' && <TeamsChannelsTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab button
// ---------------------------------------------------------------------------

interface TabButtonProps {
  readonly id: Tab;
  readonly active: boolean;
  readonly onClick: (id: Tab) => void;
  readonly children: ReactNode;
}

function TabButton({ id, active, onClick, children }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      className={`
        px-4 py-2 text-sm font-medium transition-colors
        border-b-2 -mb-px focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60
        ${active
          ? 'border-accent text-accent'
          : 'border-transparent text-fg-muted hover:text-fg hover:border-fg-muted/30'}
      `}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Tab 1 — Preferences (original T128 form)
// ---------------------------------------------------------------------------

function PreferencesTab() {
  const { data, error, isLoading, mutate } = useSWR<NotificationPrefs>(
    ADMIN_NOTIFICATIONS_PREFS_KEY,
    fetcher,
    { revalidateOnFocus: false },
  );

  if (isLoading) {
    return (
      <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-sm">Loading preferences…</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
        <p className="orb-eyebrow text-danger mb-2">request failed</p>
        <p className="text-sm text-fg">Could not load notification preferences.</p>
      </div>
    );
  }

  return <NotificationForm prefs={data} onSaved={mutate} />;
}

// ---------------------------------------------------------------------------
// Main preferences form
// ---------------------------------------------------------------------------

interface NotificationFormProps {
  readonly prefs: NotificationPrefs;
  readonly onSaved: () => void;
}

function NotificationForm({ prefs, onSaved }: NotificationFormProps) {
  const [emailEnabled, setEmailEnabled] = useState(prefs.email_enabled);
  const [slackWebhook, setSlackWebhook] = useState(prefs.slack_webhook);
  const [ntfyTopic, setNtfyTopic] = useState(prefs.ntfy_topic);
  const [subscriptions, setSubscriptions] = useState<Record<string, boolean>>(
    prefs.subscriptions ?? {},
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setEmailEnabled(prefs.email_enabled);
    setSlackWebhook(prefs.slack_webhook);
    setNtfyTopic(prefs.ntfy_topic);
    setSubscriptions(prefs.subscriptions ?? {});
  }, [prefs]);

  const toggleSubscription = (event: string) => {
    setSubscriptions((prev) => ({ ...prev, [event]: !prev[event] }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    setSaved(false);
    setSaving(true);
    try {
      const patch: NotificationPrefsPatch = {
        email_enabled: emailEnabled,
        slack_webhook: slackWebhook,
        ntfy_topic: ntfyTopic,
        subscriptions,
      };
      await adminNotificationsApi.patchPrefs(patch);
      setSaved(true);
      onSaved();
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save preferences.');
    } finally {
      setSaving(false);
    }
  };

  const events = prefs.events ?? [];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="orb-card p-5 space-y-5">
        <p className="orb-section-title text-xs">Channels</p>

        <label className="flex items-center justify-between gap-4 cursor-pointer group">
          <span className="flex items-center gap-2 text-sm text-fg">
            <Mail className="h-4 w-4 text-fg-muted" aria-hidden="true" />
            Email notifications
          </span>
          <Toggle checked={emailEnabled} onChange={setEmailEnabled} />
        </label>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="slack-webhook" className="flex items-center gap-2 text-xs text-fg-muted">
            <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
            Slack incoming webhook URL
          </label>
          <input
            id="slack-webhook"
            type="url"
            value={slackWebhook}
            onChange={(e) => setSlackWebhook(e.target.value)}
            className={inputCls}
            placeholder="https://hooks.slack.com/services/…"
          />
          <p className="text-[11px] text-fg-subtle">
            Leave empty to disable Slack notifications.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="ntfy-topic" className="flex items-center gap-2 text-xs text-fg-muted">
            <Bell className="h-3.5 w-3.5" aria-hidden="true" />
            ntfy topic
          </label>
          <input
            id="ntfy-topic"
            type="text"
            value={ntfyTopic}
            onChange={(e) => setNtfyTopic(e.target.value)}
            className={inputCls}
            placeholder="e.g. myproject-alerts"
          />
          <p className="text-[11px] text-fg-subtle">
            Messages are pushed to{' '}
            <span className="font-mono">ntfy.sh/&lt;topic&gt;</span>. Leave
            empty to disable.
          </p>
        </div>
      </section>

      {events.length > 0 && (
        <section className="orb-card p-5 space-y-3">
          <p className="orb-section-title text-xs">Event subscriptions</p>
          <p className="text-[11px] text-fg-muted">
            Toggle which events send a notification across all enabled channels.
          </p>
          <ul className="divide-y divide-border/50">
            {events.map((evt) => (
              <li
                key={evt}
                className="flex items-center justify-between py-2.5 gap-4"
              >
                <span className="font-mono text-xs text-fg">{evt}</span>
                <Toggle
                  checked={subscriptions[evt] ?? true}
                  onChange={() => toggleSubscription(evt)}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {err && (
        <p role="alert" className="text-xs text-danger">
          {err}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving} className={primaryBtnCls}>
          {(() => {
            if (saving) {
              return (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  Saving…
                </>
              );
            } else if (saved) {
              return (
                <>
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  Saved
                </>
              );
            } else {
              return (
                <>
                  <Save className="h-3.5 w-3.5" aria-hidden="true" />
                  Save preferences
                </>
              );
            }
          })()}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Shared hook for channel action handlers (used by both Slack + Teams tabs)
// ---------------------------------------------------------------------------

function useChannelActions(mutate: () => void) {
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, string>>({});
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleToggle = async (ch: NotificationChannel) => {
    try {
      await notificationChannelsApi.update(ch.id, { enabled: !ch.enabled });
      mutate();
    } catch {
      // leave UI unchanged; user will see stale state refresh
    }
  };

  const handleTest = async (id: string) => {
    setTestingId(id);
    setTestResults((prev) => ({ ...prev, [id]: '' }));
    try {
      const res = await notificationChannelsApi.test(id);
      let msg: string;
      if (res.http_status === 200) {
        msg = 'Sent ✓';
      } else if (res.error) {
        msg = `HTTP ${res.http_status}: ${res.error}`;
      } else {
        msg = `HTTP ${res.http_status}`;
      }
      setTestResults((prev) => ({ ...prev, [id]: msg }));
    } catch (e) {
      setTestResults((prev) => ({
        ...prev,
        [id]: e instanceof Error ? e.message : 'Test failed',
      }));
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await notificationChannelsApi.delete(id);
      mutate();
    } catch {
      // noop
    } finally {
      setDeletingId(null);
    }
  };

  return { testingId, testResults, deletingId, handleToggle, handleTest, handleDelete };
}

// ---------------------------------------------------------------------------
// Tab 2 — Slack Channels (T194a)
// ---------------------------------------------------------------------------

function SlackChannelsTab() {
  const { data, error, isLoading, mutate } = useSWR<NotificationChannel[]>(
    `${ADMIN_NOTIFICATION_CHANNELS_KEY}?type=slack`,
    fetcher,
    { revalidateOnFocus: false },
  );

  const [showAdd, setShowAdd] = useState(false);
  const { testingId, testResults, deletingId, handleToggle, handleTest, handleDelete } =
    useChannelActions(mutate);

  if (isLoading) {
    return (
      <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-sm">Loading channels…</span>
      </div>
    );
  }

  if (error) {
    const is402 = error?.status === 402;
    return (
      <div role="alert" className="orb-card p-6 border-warning/40 bg-warning/5">
        <p className="orb-eyebrow text-warning mb-2">
          {is402 ? 'upgrade required' : 'request failed'}
        </p>
        <p className="text-sm text-fg">
          {is402
            ? 'Slack channel management requires a Trial or Enterprise license.'
            : 'Could not load Slack channels.'}
        </p>
      </div>
    );
  }

  const channels = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-fg-muted">
          Org-wide incoming-webhook channels. Notifications are dispatched to all
          enabled channels when scan events fire.
        </p>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className={primaryBtnCls}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          Add channel
        </button>
      </div>

      {channels.length === 0 ? (
        <div className="orb-card p-8 text-center text-sm text-fg-muted">
          No Slack channels configured yet.
        </div>
      ) : (
        <ul className="orb-card divide-y divide-border/50">
          {channels.map((ch) => (
            <li key={ch.id} className="flex items-center gap-4 p-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-fg truncate">{ch.name}</p>
                <p className="font-mono text-[11px] text-fg-subtle truncate">
                  {ch.url_masked}
                </p>
                {ch.last_delivery_at && (
                  <p className="text-[11px] text-fg-muted mt-0.5">
                    Last delivery:{' '}
                    {new Date(ch.last_delivery_at).toLocaleString()}
                    {ch.last_error && (
                      <span className="text-danger ml-1">— {ch.last_error}</span>
                    )}
                  </p>
                )}
                {testResults[ch.id] && (
                  <p
                    className={`text-[11px] mt-0.5 ${
                      testResults[ch.id].startsWith('Sent')
                        ? 'text-success'
                        : 'text-danger'
                    }`}
                  >
                    {testResults[ch.id]}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <Toggle checked={ch.enabled} onChange={() => handleToggle(ch)} />

                <button
                  type="button"
                  onClick={() => handleTest(ch.id)}
                  disabled={testingId === ch.id}
                  title="Send test message"
                  className={iconBtnCls}
                >
                  {testingId === ch.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Send className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => handleDelete(ch.id)}
                  disabled={deletingId === ch.id}
                  title="Delete channel"
                  className={`${iconBtnCls} hover:text-danger`}
                >
                  {deletingId === ch.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showAdd && (
        <AddChannelDialog
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            mutate();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add channel dialog
// ---------------------------------------------------------------------------

interface AddChannelDialogProps {
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

function AddChannelDialog({ onClose, onCreated }: AddChannelDialogProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => { dialogRef.current?.showModal(); }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) { setErr('Name is required.'); return; }
    if (!url.startsWith('https://hooks.slack.com/services/')) {
      setErr('Slack webhook URL must start with https://hooks.slack.com/services/');
      return;
    }
    setSaving(true);
    try {
      const body: NotificationChannelCreate = { type: 'slack', name: name.trim(), url };
      await notificationChannelsApi.create(body);
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to create channel.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      aria-label="Add Slack channel"
      className="p-0 m-0 bg-transparent border-0 max-w-none w-screen h-screen backdrop:bg-black/40 backdrop:backdrop-blur-sm"
    >
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={onClose} aria-hidden="true" tabIndex={-1} className="absolute inset-0 h-full w-full cursor-default bg-transparent border-none" />
        <div className="relative z-10 orb-card w-full max-w-md p-6 space-y-4 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-fg">Add Slack channel</h2>
          <button
            type="button"
            onClick={onClose}
            className={iconBtnCls}
            aria-label="Close"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="chan-name" className="text-xs text-fg-muted">
              Channel name
            </label>
            <input
              id="chan-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
              placeholder="e.g. #engineering-alerts"
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="chan-url" className="text-xs text-fg-muted">
              Webhook URL
            </label>
            <input
              id="chan-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className={inputCls}
              placeholder="https://hooks.slack.com/services/…"
            />
            <p className="text-[11px] text-fg-subtle">
              Create an Incoming Webhook app in your Slack workspace and paste
              the webhook URL here. The URL is encrypted at rest.
            </p>
          </div>

          {err && (
            <p role="alert" className="text-xs text-danger">
              {err}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className={secondaryBtnCls}
            >
              Cancel
            </button>
            <button type="submit" disabled={saving} className={primaryBtnCls}>
              {saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                <>
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  Save channel
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Tab 3 — Teams Channels (T194b)
// ---------------------------------------------------------------------------

function TeamsChannelsTab() {
  const { data, error, isLoading, mutate } = useSWR<NotificationChannel[]>(
    `${ADMIN_NOTIFICATION_CHANNELS_KEY}?type=teams`,
    fetcher,
    { revalidateOnFocus: false },
  );

  const [showAdd, setShowAdd] = useState(false);
  const { testingId, testResults, deletingId, handleToggle, handleTest, handleDelete } =
    useChannelActions(mutate);

  if (isLoading) {
    return (
      <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-sm">Loading channels…</span>
      </div>
    );
  }

  if (error) {
    const is402 = error?.status === 402;
    return (
      <div role="alert" className="orb-card p-6 border-warning/40 bg-warning/5">
        <p className="orb-eyebrow text-warning mb-2">
          {is402 ? 'upgrade required' : 'request failed'}
        </p>
        <p className="text-sm text-fg">
          {is402
            ? 'Teams channel management requires a Trial or Enterprise license.'
            : 'Could not load Teams channels.'}
        </p>
      </div>
    );
  }

  const channels = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-fg-muted">
          Org-wide Microsoft Teams channels. Notifications are dispatched as
          Adaptive Cards to all enabled channels when scan events fire.
        </p>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className={primaryBtnCls}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          Add channel
        </button>
      </div>

      {channels.length === 0 ? (
        <div className="orb-card p-8 text-center text-sm text-fg-muted">
          No Teams channels configured yet.
        </div>
      ) : (
        <ul className="orb-card divide-y divide-border/50">
          {channels.map((ch) => (
            <li key={ch.id} className="flex items-center gap-4 p-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-fg truncate">{ch.name}</p>
                <p className="font-mono text-[11px] text-fg-subtle truncate">
                  {ch.url_masked}
                </p>
                {ch.last_delivery_at && (
                  <p className="text-[11px] text-fg-muted mt-0.5">
                    Last delivery:{' '}
                    {new Date(ch.last_delivery_at).toLocaleString()}
                    {ch.last_error && (
                      <span className="text-danger ml-1">— {ch.last_error}</span>
                    )}
                  </p>
                )}
                {testResults[ch.id] && (
                  <p
                    className={`text-[11px] mt-0.5 ${
                      testResults[ch.id].startsWith('Sent')
                        ? 'text-success'
                        : 'text-danger'
                    }`}
                  >
                    {testResults[ch.id]}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <Toggle checked={ch.enabled} onChange={() => handleToggle(ch)} />

                <button
                  type="button"
                  onClick={() => handleTest(ch.id)}
                  disabled={testingId === ch.id}
                  title="Send test message"
                  className={iconBtnCls}
                >
                  {testingId === ch.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Send className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => handleDelete(ch.id)}
                  disabled={deletingId === ch.id}
                  title="Delete channel"
                  className={`${iconBtnCls} hover:text-danger`}
                >
                  {deletingId === ch.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showAdd && (
        <AddTeamsChannelDialog
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            mutate();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Teams channel dialog
// ---------------------------------------------------------------------------

interface AddTeamsChannelDialogProps {
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

function AddTeamsChannelDialog({ onClose, onCreated }: AddTeamsChannelDialogProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => { dialogRef.current?.showModal(); }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) { setErr('Name is required.'); return; }
    if (!url.includes('.webhook.office.com/') && !url.includes('.outlook.office.com/')) {
      setErr('Teams webhook URL must be a webhook.office.com or outlook.office.com URL.');
      return;
    }
    setSaving(true);
    try {
      const body: NotificationChannelCreate = { type: 'teams', name: name.trim(), url };
      await notificationChannelsApi.create(body);
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to create channel.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      aria-label="Add Teams channel"
      className="p-0 m-0 bg-transparent border-0 max-w-none w-screen h-screen backdrop:bg-black/40 backdrop:backdrop-blur-sm"
    >
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={onClose} aria-hidden="true" tabIndex={-1} className="absolute inset-0 h-full w-full cursor-default bg-transparent border-none" />
        <div className="relative z-10 orb-card w-full max-w-md p-6 space-y-4 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-fg">Add Teams channel</h2>
          <button
            type="button"
            onClick={onClose}
            className={iconBtnCls}
            aria-label="Close"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="teams-chan-name" className="text-xs text-fg-muted">
              Channel name
            </label>
            <input
              id="teams-chan-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
              placeholder="e.g. Engineering Alerts"
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="teams-chan-url" className="text-xs text-fg-muted">
              Incoming Webhook URL
            </label>
            <input
              id="teams-chan-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className={inputCls}
              placeholder="https://…webhook.office.com/webhookb2/…"
            />
            <p className="text-[11px] text-fg-subtle">
              In Teams, go to a channel → Manage channel → Connectors → Incoming
              Webhook, or use a Power Automate flow. Paste the generated URL here.
              The URL is encrypted at rest.
            </p>
          </div>

          {err && (
            <p role="alert" className="text-xs text-danger">
              {err}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className={secondaryBtnCls}
            >
              Cancel
            </button>
            <button type="submit" disabled={saving} className={primaryBtnCls}>
              {saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                <>
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  Save channel
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Toggle switch
// ---------------------------------------------------------------------------

interface ToggleProps {
  readonly checked: boolean;
  readonly onChange: (v: boolean) => void;
}

function Toggle({ checked, onChange }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`
        relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2
        border-transparent transition-colors duration-150 focus:outline-none
        focus-visible:ring-2 focus-visible:ring-accent/60
        ${checked ? 'bg-accent' : 'bg-fg-subtle/40'}
      `}
    >
      <span
        aria-hidden="true"
        className={`
          inline-block h-4 w-4 rounded-full bg-white shadow
          transform transition-transform duration-150
          ${checked ? 'translate-x-4' : 'translate-x-0'}
        `}
      />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const inputCls = `
  w-full px-3 py-2 rounded-md border border-border
  bg-bg-inset text-fg text-sm placeholder-fg-subtle
  focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
  transition-colors
`.trim();

const primaryBtnCls = `
  inline-flex items-center gap-1.5 px-4 py-2 rounded-md
  text-xs font-semibold uppercase tracking-[0.08em]
  bg-accent text-accent-fg hover:opacity-90
  border border-accent/40 transition-opacity
  disabled:opacity-30 disabled:cursor-not-allowed
`.trim();

const secondaryBtnCls = `
  inline-flex items-center gap-1.5 px-4 py-2 rounded-md
  text-xs font-semibold uppercase tracking-[0.08em]
  bg-bg-inset text-fg-muted hover:text-fg
  border border-border transition-colors
  disabled:opacity-30 disabled:cursor-not-allowed
`.trim();

const iconBtnCls = `
  inline-flex items-center justify-center h-7 w-7 rounded-md
  text-fg-muted hover:text-fg hover:bg-bg-inset
  transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60
  disabled:opacity-30 disabled:cursor-not-allowed
`.trim();
